import React, { useState, AriaRole, ElementType, forwardRef } from 'react';
import classNames from 'classnames';
import { TagProps } from './types';
import styles from './index.module.scss';

const Tag = forwardRef<HTMLElement, TagProps>(
  ({ size = 'md', variant = 'filled', onClick, href, children, target, ariaLabel, hideOnClick }: TagProps, ref) => {
    const [isOpen, setIsOpen] = useState(true);
    const hasClickEvent = onClick || hideOnClick;

    const getElement = (): { element: ElementType; role?: AriaRole; tabIndex?: number } => {
      if (href) {
        return {
          element: 'a',
          role: 'link',
        };
      }

      if (hasClickEvent) {
        return {
          element: 'button',
          role: 'button',
        };
      }

      return { element: 'div' };
    };

    const onTagClick = (e) => {
      if (hideOnClick) {
        setIsOpen(false);
      }

      if (typeof onClick === 'function') {
        onClick(e);
      }
    };

    const { element: Element, role } = getElement();
    const isInteractive = Boolean(role);

    const props = {
      onClick: hasClickEvent ? onTagClick : undefined,
      className: classNames(styles.tag, styles[`size-${size}`], styles[`variant-${variant}`], {
        [styles.interactive]: isInteractive,
      }),
      href,
      role,
      target,
      'aria-label': ariaLabel,
    };

    if (!isOpen) {
      return null;
    }

    return (
      <Element ref={ref} {...props}>
        {children}
      </Element>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
